import React, { useState, useEffect, useMemo } from 'react';
import { ordersAPI } from '../services/api';
import './Panel.css';

const OrderCard: React.FC<{order: Order, activeTab: string, onConfirmAction: (type: string, orderId: number, orderData?: any) => void}> = ({ order, activeTab, onConfirmAction }) => {
  const handleComplete = () => {
    const address = order.street_address ? `${order.street_address}, ${order.city}, ${order.state || 'MO'} ${order.zipcode}` : order.delivery_address;
    onConfirmAction('complete', order.id, { address });
  };

  const handleCancel = () => {
    onConfirmAction('cancel', order.id);
  };

  const handleMarkPaid = () => {
    onConfirmAction('paid', order.id, { total: parseFloat(order.total_amount.toString()).toFixed(2) });
  };

  return (
    <div className={`order-card ${order.is_paid ? 'paid-card' : 'unpaid-card'}`}>
      <div className="payment-status-badge">
        <span className={`payment-badge ${order.is_paid ? 'paid' : 'unpaid'}`}>
          {order.is_paid ? 'PAID' : 'UNPAID'}
        </span>
      </div>
      <div className="order-header">
        <div className="order-info">
          <h3>Order #{order.order_number}</h3>
          <p className="order-date">
            {new Date(order.created_at).toLocaleDateString()}
          </p>
        </div>
        <div className="order-total">
          ${parseFloat(order.total_amount.toString()).toFixed(2)}
        </div>
      </div>

      <div className="order-details">
        <div className="customer-info">
          <h4>Customer</h4>
          <p>{order.first_name && order.last_name 
            ? `${order.first_name} ${order.last_name}` 
            : order.guest_name}</p>
          <p>{order.email || order.guest_email}</p>
        </div>

        <div className="delivery-info">
          <h4>Delivery Address</h4>
          {order.street_address ? (
            <div>
              <p>{order.street_address}</p>
              <p>{order.city}, {order.state || 'MO'} {order.zipcode}</p>
            </div>
          ) : (
            <p>{order.delivery_address}</p>
          )}
        </div>

        <div className="order-items">
          <h4>Items</h4>
          {order.items.map((item, index) => (
            <div key={index} className="order-item">
              <span>{item.quantity}x {item.name}</span>
              <span>${(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
        </div>
      </div>

      {order.delivery_notes && (
        <div className="delivery-notes-display">
          <h4>📝 Delivery Notes</h4>
          <div className="notes-content">
            {order.delivery_notes}
          </div>
        </div>
      )}

      <div className="order-details">
      </div>

      <div className="order-actions">
        <div className="quick-actions">
          {activeTab === 'active' && (
            <>
              <button onClick={handleComplete} className="quick-btn complete">
                Mark Complete
              </button>
              <button onClick={handleCancel} className="quick-btn cancel">
                Cancel
              </button>
            </>
          )}
          {!order.is_paid && (
            <button onClick={handleMarkPaid} className="quick-btn paid">
              Mark Paid
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

interface Order {
  id: number;
  order_number: string;
  total_amount: number;
  status: string;
  is_paid?: boolean;
  delivery_address: string;
  street_address?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  delivery_notes?: string;
  created_at: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  guest_name?: string;
  guest_email?: string;
  guest_phone?: string;
  items: Array<{
    name: string;
    quantity: number;
    price: number;
  }>;
}

const AssociatePanel: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'active' | 'completed' | 'cancelled'>('active');
  const [sortByZip, setSortByZip] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{type: string, orderId: number, orderData?: any} | null>(null);
  const [showTotals, setShowTotals] = useState(false);
  const currentYear = new Date().getFullYear();
  const [yearFilter, setYearFilter] = useState<string>(String(currentYear));

  useEffect(() => {
    fetchOrders();
  }, []);

  const getOrderYear = (order: Order): number => new Date(order.created_at).getFullYear();

  // Years that actually have orders, newest first. The current year is always
  // offered so the default filter is selectable before the season's first order.
  const availableYears = useMemo(() => {
    const years = new Set<number>([currentYear]);
    orders.forEach(order => {
      const year = getOrderYear(order);
      if (!Number.isNaN(year)) years.add(year);
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [orders, currentYear]);

  // Every downstream view (tab counts, list, ZIP grouping, totals, exports)
  // reads from this so the year filter applies consistently.
  const yearOrders = useMemo(() => {
    if (yearFilter === 'all') return orders;
    const year = parseInt(yearFilter, 10);
    return orders.filter(order => getOrderYear(order) === year);
  }, [orders, yearFilter]);

  const yearLabel = yearFilter === 'all' ? 'All Years' : yearFilter;
  const exportYearToken = yearFilter === 'all' ? 'All_Years' : yearFilter;

  const fetchOrders = async () => {
    try {
      const response = await ordersAPI.getAll();
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId: number, status: string) => {
    try {
      await ordersAPI.updateStatus(orderId, status);
      fetchOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const updatePaymentStatus = async (orderId: number, isPaid: boolean) => {
    try {
      await ordersAPI.updatePaymentStatus(orderId, isPaid);
      fetchOrders();
    } catch (error) {
      console.error('Error updating payment status:', error);
    }
  };

  const exportOrders = (format: 'xlsx' | 'csv') => {
    const activeOrders = getOrdersByTab();
    
    const exportData = activeOrders.map(order => {
      const customerName = order.first_name && order.last_name 
        ? `${order.first_name} ${order.last_name}` 
        : order.guest_name || 'N/A';
      const customerEmail = order.email || order.guest_email || 'N/A';
      const customerPhone = order.guest_phone || 'N/A';
      const itemsList = order.items.map(item => `${item.quantity}x ${item.name}`).join(', ');
      
      return {
        'Order Number': order.order_number,
        'Customer Name': customerName,
        'Phone': customerPhone,
        'Email': customerEmail,
        'Address': order.delivery_address,
        'State': order.state || 'MO',
        'ZIP Code': order.zipcode || 'N/A',
        'Items': itemsList,
        'Delivery Notes': order.delivery_notes || 'None',
        'Total': `$${parseFloat(order.total_amount.toString()).toFixed(2)}`,
        'Payment Status': order.is_paid ? 'PAID' : 'UNPAID',
        'Order Status': order.status.toUpperCase(),
        'Order Date': new Date(order.created_at).toLocaleDateString()
      };
    });

    if (format === 'csv') {
      downloadCSV(exportData);
    } else {
      downloadExcel(exportData);
    }
  };

  const downloadCSV = (data: any[]) => {
    if (data.length === 0) {
      alert(`No active orders to export for ${yearLabel}`);
      return;
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => `"${row[header]}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `TFF_Active_Orders_${exportYearToken}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadExcel = (data: any[]) => {
    if (data.length === 0) {
      alert(`No active orders to export for ${yearLabel}`);
      return;
    }

    // Simple Excel format using HTML table
    const headers = Object.keys(data[0]);
    const excelContent = `
      <table>
        <tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
        ${data.map(row => 
          `<tr>${headers.map(h => `<td>${row[h]}</td>`).join('')}</tr>`
        ).join('')}
      </table>
    `;

    const blob = new Blob([excelContent], { 
      type: 'application/vnd.ms-excel' 
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `TFF_Active_Orders_${exportYearToken}_${new Date().toISOString().split('T')[0]}.xls`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleConfirmAction = (type: string, orderId: number, orderData?: any) => {
    setConfirmAction({ type, orderId, orderData });
  };

  const executeAction = async () => {
    if (!confirmAction) return;
    
    const { type, orderId, orderData } = confirmAction;
    
    try {
      switch (type) {
        case 'complete':
          await updateOrderStatus(orderId, 'completed');
          break;
        case 'cancel':
          await updateOrderStatus(orderId, 'cancelled');
          break;
        case 'paid':
          await updatePaymentStatus(orderId, true);
          break;
      }
    } catch (error) {
      console.error('Error executing action:', error);
    }
    
    setConfirmAction(null);
  };

  const getOrdersByTab = () => {
    switch (activeTab) {
      case 'active':
        return yearOrders.filter(order => !['completed', 'cancelled'].includes(order.status));
      case 'completed':
        return yearOrders.filter(order => order.status === 'completed');
      case 'cancelled':
        return yearOrders.filter(order => order.status === 'cancelled');
      default:
        return yearOrders;
    }
  };

  const filteredOrders = getOrdersByTab().filter(order => {
    const searchLower = searchTerm.toLowerCase();
    const customerName = order.first_name && order.last_name 
      ? `${order.first_name} ${order.last_name}` 
      : order.guest_name || '';
    const customerEmail = order.email || order.guest_email || '';
    
    return (
      order.order_number.toLowerCase().includes(searchLower) ||
      customerName.toLowerCase().includes(searchLower) ||
      customerEmail.toLowerCase().includes(searchLower) ||
      order.delivery_address.toLowerCase().includes(searchLower) ||
      (order.zipcode && order.zipcode.includes(searchLower))
    );
  });

  const getOrdersByZipcode = () => {
    if (!sortByZip) return filteredOrders;
    
    const ordersByZip: {[key: string]: Order[]} = {};
    filteredOrders.forEach(order => {
      const zip = order.zipcode || 'Unknown';
      if (!ordersByZip[zip]) ordersByZip[zip] = [];
      ordersByZip[zip].push(order);
    });
    
    return ordersByZip;
  };

  const calculateTotals = () => {
    const activeOrders = yearOrders.filter(order => !['completed', 'cancelled'].includes(order.status));
    
    const productTotals: {[key: string]: {quantity: number, revenue: number}} = {};
    let totalRevenue = 0;
    let paidRevenue = 0;
    let unpaidRevenue = 0;
    let totalOrders = activeOrders.length;
    let paidOrders = 0;
    let unpaidOrders = 0;
    
    activeOrders.forEach(order => {
      const orderTotal = parseFloat(order.total_amount.toString());
      totalRevenue += orderTotal;
      
      if (order.is_paid) {
        paidRevenue += orderTotal;
        paidOrders++;
      } else {
        unpaidRevenue += orderTotal;
        unpaidOrders++;
      }
      
      order.items.forEach(item => {
        if (!productTotals[item.name]) {
          productTotals[item.name] = {quantity: 0, revenue: 0};
        }
        productTotals[item.name].quantity += item.quantity;
        productTotals[item.name].revenue += item.price * item.quantity;
      });
    });
    
    return {
      productTotals,
      totalRevenue,
      paidRevenue,
      unpaidRevenue,
      totalOrders,
      paidOrders,
      unpaidOrders
    };
  };

  if (loading) {
    return <div className="loading">Loading orders...</div>;
  }

  return (
    <div className="admin-panel">
      <div className="panel-header">
        <h1>Orders Panel</h1>
        <p>Manage customer orders and fulfillment!</p>
      </div>

      <div className="order-tabs">
        <button 
          className={`tab-btn ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => setActiveTab('active')}
        >
          Active Orders ({yearOrders.filter(o => !['completed', 'cancelled'].includes(o.status)).length})
        </button>
        <button 
          className={`tab-btn ${activeTab === 'completed' ? 'active' : ''}`}
          onClick={() => setActiveTab('completed')}
        >
          Completed ({yearOrders.filter(o => o.status === 'completed').length})
        </button>
        <button 
          className={`tab-btn ${activeTab === 'cancelled' ? 'active' : ''}`}
          onClick={() => setActiveTab('cancelled')}
        >
          Cancelled ({yearOrders.filter(o => o.status === 'cancelled').length})
        </button>
      </div>

      <div className="section-header">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search by order number, customer, email, or address..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="year-filter">
          <label htmlFor="order-year-filter">Order Year:</label>
          <select
            id="order-year-filter"
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            className="year-select"
          >
            {availableYears.map(year => (
              <option key={year} value={String(year)}>{year}</option>
            ))}
            <option value="all">All Years</option>
          </select>
        </div>
        {activeTab === 'active' && (
          <div className="sort-controls">
            <label className="sort-toggle">
              <input
                type="checkbox"
                checked={sortByZip}
                onChange={(e) => setSortByZip(e.target.checked)}
              />
              Sort by ZIP Code
            </label>
            <div className="export-controls">
              <button onClick={() => setShowTotals(true)} className="export-btn totals">
                📈 Show Totals
              </button>
              <button onClick={() => exportOrders('xlsx')} className="export-btn">
                📊 Export Excel
              </button>
              <button onClick={() => exportOrders('csv')} className="export-btn csv">
                📄 Export CSV
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="orders-container">
        {sortByZip && activeTab === 'active' ? (
          Object.entries(getOrdersByZipcode()).map(([zipcode, zipOrders]) => (
            <div key={zipcode} className="zip-section">
              <h3 className="zip-header">ZIP Code: {zipcode} ({zipOrders.length} orders)</h3>
              <div className="orders-list">
                {zipOrders.map((order: Order) => (
                  <OrderCard key={order.id} order={order} activeTab={activeTab} onConfirmAction={handleConfirmAction} />
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className="orders-list">
            {filteredOrders.map((order: Order) => (
              <OrderCard key={order.id} order={order} activeTab={activeTab} onConfirmAction={handleConfirmAction} />
            ))}
          </div>
        )}
      </div>

      {confirmAction && (
        <div className="confirm-modal-overlay">
          <div className="confirm-modal">
            <h3>
              {confirmAction.type === 'complete' && 'Confirm Delivery'}
              {confirmAction.type === 'cancel' && 'Cancel Order'}
              {confirmAction.type === 'paid' && 'Mark as Paid'}
            </h3>
            <p>
              {confirmAction.type === 'complete' && `Has this order been delivered to ${confirmAction.orderData?.address}?`}
              {confirmAction.type === 'cancel' && 'Are you sure you want to cancel this order?'}
              {confirmAction.type === 'paid' && `Are you sure this customer has paid $${confirmAction.orderData?.total} for this order?`}
            </p>
            <div className="confirm-actions">
              <button onClick={() => setConfirmAction(null)} className="cancel-btn">No</button>
              <button onClick={executeAction} className="confirm-btn">Yes</button>
            </div>
          </div>
        </div>
      )}

      {filteredOrders.length === 0 && (
        <div className="no-results">
          <h3>No {activeTab} orders found for {yearLabel}</h3>
          <p>
            {searchTerm
              ? 'Try adjusting your search criteria, or change the Order Year filter.'
              : 'Change the Order Year filter above to see orders from a previous season.'}
          </p>
        </div>
      )}

      {showTotals && (() => {
        const totals = calculateTotals();
        return (
          <div className="totals-modal-overlay">
            <div className="totals-modal">
              <div className="totals-header">
                <h3>📈 Active Orders Summary — {yearLabel}</h3>
                <button onClick={() => setShowTotals(false)} className="close-btn">×</button>
              </div>
              
              <div className="totals-content">
                <div className="totals-section">
                  <h4>💰 Revenue Summary</h4>
                  <div className="revenue-grid">
                    <div className="revenue-item total">
                      <span>Total Revenue:</span>
                      <span>${totals.totalRevenue.toFixed(2)}</span>
                    </div>
                    <div className="revenue-item paid">
                      <span>Paid Revenue:</span>
                      <span>${totals.paidRevenue.toFixed(2)}</span>
                    </div>
                    <div className="revenue-item unpaid">
                      <span>Unpaid Revenue:</span>
                      <span>${totals.unpaidRevenue.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="totals-section">
                  <h4>📋 Order Summary</h4>
                  <div className="order-stats">
                    <div className="stat-item">
                      <span>Total Orders:</span>
                      <span>{totals.totalOrders}</span>
                    </div>
                    <div className="stat-item">
                      <span>Paid Orders:</span>
                      <span>{totals.paidOrders}</span>
                    </div>
                    <div className="stat-item">
                      <span>Unpaid Orders:</span>
                      <span>{totals.unpaidOrders}</span>
                    </div>
                  </div>
                </div>
                
                <div className="totals-section">
                  <h4>🌿 Product Totals</h4>
                  <div className="product-totals">
                    {Object.entries(totals.productTotals)
                      .sort(([,a], [,b]) => b.quantity - a.quantity)
                      .map(([productName, data]) => (
                      <div key={productName} className="product-total-item">
                        <div className="product-name">{productName}</div>
                        <div className="product-stats">
                          <span>{data.quantity} units</span>
                          <span>${data.revenue.toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default AssociatePanel;